import { UTCDateMini } from '@date-fns/utc';
import { addDays, endOfDay, endOfMinute, format, parse, startOfDay, startOfISOWeek, subHours } from 'date-fns';

import { API_DATE_FORMAT } from 'shared/lib/datetime/formatDate';

export type FilterTimezone = 'utc' | 'local';

export const PeriodType = {
  Last24Hours: 'last_24h',
  Last7Days: 'last_7d',
  ThisWeek: 'this_week',
  Last30Days: 'last_30d',
  Last90Days: 'last_90d',
  Custom: 'custom',
} as const;

export type PeriodType = (typeof PeriodType)[keyof typeof PeriodType];

const PERIOD_TYPE_VALUES = new Set<string>(Object.values(PeriodType));

export const getPeriodTypeFromCode = (code: string): PeriodType | undefined =>
  PERIOD_TYPE_VALUES.has(code) ? (code as PeriodType) : undefined;

export type PresetPeriodType = Exclude<PeriodType, typeof PeriodType.Custom>;

export const isPresetPeriod = (period: PeriodType): period is PresetPeriodType => period !== PeriodType.Custom;

// Local timezone, so a picked day matches the locally-rendered "Created at" column.
const getNow = () => new Date();

const periodStart = (days: number) => format(addDays(startOfDay(getNow()), days), API_DATE_FORMAT);

const periodEnd = (days: number) => format(addDays(endOfDay(getNow()), days), API_DATE_FORMAT);

const weekStart = () => format(startOfISOWeek(getNow()), API_DATE_FORMAT);

export const getPeriodForFilter = (period: PresetPeriodType): [string, string] => {
  switch (period) {
    case PeriodType.Last24Hours:
      return [periodStart(0), periodEnd(0)];
    case PeriodType.Last7Days:
      return [periodStart(-6), periodEnd(0)];
    case PeriodType.ThisWeek:
      return [weekStart(), periodEnd(0)];
    case PeriodType.Last30Days:
      return [periodStart(-29), periodEnd(0)];
    case PeriodType.Last90Days:
      return [periodStart(-89), periodEnd(0)];
  }
};

export const getPresetPeriodRange = (code?: string): [string, string] | null => {
  const periodType = code ? getPeriodTypeFromCode(code) : undefined;
  return periodType && isPresetPeriod(periodType) ? getPeriodForFilter(periodType) : null;
};

export const getLast24HoursRange = (): [string, string] => {
  const now = endOfMinute(getNow());
  return [subHours(now, 24).toISOString(), now.toISOString()];
};

// date-fns day math respects the Date subclass, so a UTCDateMini reference yields UTC-day
// boundaries while a plain Date yields local-day boundaries.
const parseDay = (date: string, timezone: FilterTimezone) => {
  if (date.includes('T')) {
    return timezone === 'utc' ? new UTCDateMini(date) : new Date(date);
  }

  return parse(date, API_DATE_FORMAT, timezone === 'utc' ? new UTCDateMini() : new Date());
};

const resolveStart = ({
  from,
  timezone,
}: {
  from: string | undefined;
  timezone: FilterTimezone;
}): string | undefined => (from ? startOfDay(parseDay(from, timezone)).toISOString() : undefined);

// Exclusive `_lt` → start of the next day; inclusive `_lte` → end of the day (in `timezone`).
const resolveEnd = ({
  to,
  endBound,
  timezone,
}: {
  to: string | undefined;
  endBound: 'lt' | 'lte';
  timezone: FilterTimezone;
}): string | undefined => {
  if (!to) {
    return undefined;
  }

  const day = parseDay(to, timezone);

  return endBound === 'lt' ? startOfDay(addDays(day, 1)).toISOString() : endOfDay(day).toISOString();
};

type ResolveApiRangeArgs = {
  period: string | undefined;
  from: string | undefined;
  to: string | undefined;
  endBound: 'lt' | 'lte';
  timezone?: FilterTimezone;
};

export function resolveApiRange(args: ResolveApiRangeArgs & { from: string; to: string }): [string, string];
export function resolveApiRange(args: ResolveApiRangeArgs): [string | undefined, string | undefined];
export function resolveApiRange({
  period,
  from,
  to,
  endBound,
  timezone = 'local',
}: ResolveApiRangeArgs): [string | undefined, string | undefined] {
  return period === PeriodType.Last24Hours
    ? getLast24HoursRange()
    : [resolveStart({ from, timezone }), resolveEnd({ to, endBound, timezone })];
}

type ResolvePeriodParams = {
  period?: string;
  date_from?: string;
  date_to?: string;
};

type ResolvedPeriod = {
  period: string;
  date_from: string;
  date_to: string;
};

export const resolvePeriodFromParams = (
  params: ResolvePeriodParams,
  { defaultPeriod = PeriodType.Last30Days }: { defaultPeriod?: PresetPeriodType } = {},
): ResolvedPeriod => {
  const { period, date_from, date_to } = params;

  if (period) {
    const periodType = getPeriodTypeFromCode(period);
    if (periodType && isPresetPeriod(periodType)) {
      const [from, to] = getPeriodForFilter(periodType);
      return { period: periodType, date_from: from, date_to: to };
    }
  }

  if (date_from && date_to) {
    return { period: PeriodType.Custom, date_from, date_to };
  }

  const [from, to] = getPeriodForFilter(defaultPeriod);
  return { period: defaultPeriod, date_from: from, date_to: to };
};
